import { useRef, useState } from "react";
import { Info, Loader2 } from "lucide-react";
import { canSubmitRequired } from "../lib/requiredFields";
import { ConfirmedRow, DisclosureRow, CounterBox, Field, FieldError, ToggleSwitch, ActionButton, SecondaryAction } from "./kit";
import GroundPicker from "./GroundPicker";
import FacilityPicker from "./FacilityPicker";
import { rateOrNull, NOT_RECORDED } from "../lib/counters";
import { economyOrNull } from "../lib/overs";
import { facilityAfterGroundChange } from "../lib/facilities";
import { markNav } from "../lib/usageTracker";
import { useFocusFirstInvalid } from "../lib/focusFirstInvalid";
import {
  LOG_STEP2_BACK_LABEL,
  GROUND_EYEBROW,
  CHANGE_LABEL,
  MORE_TITLE,
  MORE_SUBLABEL,
  COMP_HELPER,
  OPP_PLACEHOLDER,
  BATTING_LABEL,
  STRIKE_RATE_LABEL,
  BOWLING_LABEL,
  RUNS_CONCEDED_LABEL,
  ECONOMY_LABEL,
  FIELDING_LABEL,
  FIELDING_HELP,
  KEEPER_LABEL,
  KEEPER_SUBLABEL,
  KEEPER_GROUP_LABEL,
  KEEPER_GROUP_HELP,
  CATCHES_ZERO_NOTE,
  FOOTER_NOTE,
  SAVE_LABEL,
  SAVE_LABEL_EDIT,
  SAVING_LABEL,
  REQUIRED_SUMMARY,
  SAVE_FAILED_TITLE,
  SAVE_FAILED_BODY,
  EDIT_EYEBROW,
  EDIT_TITLE,
  FINISH_EYEBROW,
  FINISH_TITLE,
  FINISH_LEDE,
  SAVE_LABEL_FINISH,
  FINISH_SAVE_FAILED,
  FINISH_SAVE_FAILED_BODY,
  strikeRateHelpText,
  economyHelpText,
  formatRateDisplay,
} from "../lib/logScorecardStep";

// #1373 (D382, D382a): Cancel's drawn text is 12px at a 1.5 line; every term
// below that depends on the line is derived from these two constants.
const CANCEL_FONT_PX = 12;
const CANCEL_LINE_PX = CANCEL_FONT_PX * 1.5;
// Cancel's 44px tap box starts at the field's bottom edge (the stack gap above
// the text is inside it), so the list opens a tap box plus its own gap below
// the field. The list is out of flow: nothing the board drew moves.
const CANCEL_ROW_CLEARANCE = "calc(var(--nw-size-tap) + var(--nw-space-xtight))";
// At rest the box's footprint is gap + one line (what the plain link took), so
// the rows below do not move. While the list is open the box is free of the
// Date label only if those rows sit 8px lower (D382a): the tap token less the
// 36px between the field and that label.
const CANCEL_FOOTPRINT = `calc(${CANCEL_LINE_PX}px + var(--nw-space-xtight) - var(--nw-size-tap))`;
const CANCEL_OPEN_SHIFT = "8px";
const DISMISSAL_TYPES = ["Not Out", "Bowled", "Caught", "LBW", "Run Out", "Stumped", "Hit Wicket", "Retired", "Obstructing Field"];

/** The 46px bordered box every field label sits above — `.nw-label` + a
 * plain 15px value box, `DirectionA2.dc.html:67` etc. */
function Group({ children, style, ...rest }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nw-space-xtight)", ...style }} {...rest}>
      {children}
    </div>
  );
}

/**
 * Review round 3 (#907, F1): a real `<label htmlFor>`, not the untied
 * `<div>` this used to be — the same `nw-label` styling, plus an explicit
 * `display: "block"` (a `<label>` defaults to inline where the `<div>` it
 * replaces defaulted to block), matching `kit/Field.jsx`'s own fix for the
 * identical bug. `htmlFor` is required, not optional: every call site
 * below pairs one of these with exactly one field, so there is always a
 * concrete id to point at.
 */
function GroupLabel({ htmlFor, children }) {
  return (
    <label htmlFor={htmlFor} className="nw-label" style={{ display: "block" }}>
      {children}
    </label>
  );
}

/**
 * A counter box with its own label above it — the shape used 15 times on
 * this screen (batting/bowling/fielding/keeping). `note`, when given, is
 * the build-only #715 line beside the two catches boxes.
 *
 * Review round 3, final narrow round (A3), F9: the label used to be a
 * plain untied `<div>` — the same bug #902/#907 fixed everywhere else on
 * this screen, one component over. `data-testid` doubles as the id (the
 * `register-guardian-confirm` convention this codebase already uses
 * elsewhere), so a real `<label htmlFor>` ties to it.
 */
function Counter({ label, note, "data-testid": testId, ...rest }) {
  return (
    <Group>
      <label htmlFor={testId} style={{ fontSize: 11, color: "var(--nw-ink-muted)", display: "block" }}>{label}</label>
      <CounterBox id={testId} data-testid={testId} {...rest} />
      {note && <div className="nw-foot">{note}</div>}
    </Group>
  );
}

/** `m-bat-pos` — still a 1-11 dropdown (issue #127's own reason: free text
 * would let a batting position be typed as anything), styled to sit flush
 * with the CounterBox tiles beside it (DirectionA2.dc.html:86).
 *
 * Review round 3, final narrow round (A3), F9: real `<label htmlFor>`,
 * same fix as `Counter`'s own, just above.
 */
function BattingPositionSelect({ value, onChange, "data-testid": testId }) {
  return (
    <Group>
      <label htmlFor={testId} style={{ fontSize: 11, color: "var(--nw-ink-muted)", display: "block" }}>Batted at</label>
      <select
        id={testId}
        data-testid={testId}
        value={String(value)}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: "100%",
          height: "var(--nw-size-control)",
          borderRadius: "var(--nw-radius-control)",
          border: "1px solid var(--nw-line)",
          background: "var(--nw-surface)",
          textAlign: "center",
          fontFamily: "'Manrope', sans-serif",
          fontWeight: 800,
          fontSize: 20,
          color: "var(--nw-ink)",
        }}
      >
        {Array.from({ length: 11 }, (_, i) => i + 1).map((n) => (
          <option key={n} value={n}>{n}</option>
        ))}
      </select>
    </Group>
  );
}

/** `m-dismissal` — a category, not a number: 14px, not Manrope/bold
 * (DirectionA2.dc.html:93, contrast against the Manrope-800 counters
 * beside it).
 *
 * Review round 3, final narrow round (A3), F9: real `<label htmlFor>`,
 * same fix as `Counter`'s own, above.
 */
function DismissalSelect({ value, onChange, "data-testid": testId }) {
  return (
    <Group>
      <label htmlFor={testId} style={{ fontSize: 11, color: "var(--nw-ink-muted)", display: "block" }}>Out</label>
      <select
        id={testId}
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: "100%",
          height: "var(--nw-size-control)",
          borderRadius: "var(--nw-radius-control)",
          border: "1px solid var(--nw-line)",
          background: "var(--nw-surface)",
          textAlign: "center",
          fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
          fontSize: 14,
          color: "var(--nw-ink)",
        }}
      >
        {DISMISSAL_TYPES.map((d) => (
          <option key={d} value={d}>{d}</option>
        ))}
      </select>
    </Group>
  );
}

/** The "worked out for you" row — label left, Manrope-800/16px figure
 * right, baseline-aligned (DirectionA2.dc.html:99-101,121-123). Not the
 * kit `NumberTile`: none of its four variants draw a 16px figure or a
 * plain (non-bold, non-uppercase) 12px label — this is the one place on
 * the screen that needed its own markup rather than a kit composition;
 * flagged in the PR. */
function DerivedRateRow({ label, display, "data-testid": testId }) {
  const isAbsent = display === NOT_RECORDED;
  return (
    <div className="flex items-baseline justify-between" style={{ gap: 10 }}>
      <span style={{ fontSize: 12, color: "var(--nw-ink-muted)" }}>{label}</span>
      <span
        data-testid={testId}
        style={{
          fontFamily: "'Manrope', sans-serif",
          fontWeight: 800,
          fontSize: 16,
          color: isAbsent ? "var(--nw-ink-muted)" : "var(--nw-ink)",
        }}
      >
        {display}
      </span>
    </div>
  );
}

function Card({ children }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--nw-space-inline)",
        padding: "var(--nw-space-card)",
        background: "var(--nw-surface)",
        border: "1px solid var(--nw-line)",
        borderRadius: "var(--nw-radius-card)",
      }}
    >
      {children}
    </div>
  );
}

function SectionLabel({ children }) {
  return <div className="nw-section">{children}</div>;
}

function ErrorBanner({ children, testId }) {
  return (
    <div
      data-testid={testId}
      style={{
        fontSize: 13,
        lineHeight: 1.5,
        color: "var(--nw-ink)",
        padding: "10px 12px",
        background: "var(--nw-line-faint)",
        borderLeft: "3px solid var(--nw-alert)",
        borderRadius: "var(--nw-radius-control)",
      }}
    >
      {children}
    </div>
  );
}

/**
 * Issue #758 — step 2 of 3, the scorecard. Matched against
 * `DirectionA2.dc.html` (design-round-5, sha256 `1e29fb61…`) — see
 * `brief-758.md` for where the issue text and the drawing were reconciled.
 *
 * `mode`: "create" | "edit" | "finish" — see `LogScorecardFlow.jsx`'s own header.
 */
export default function LogMatchScorecardStep({
  mode,
  form,
  setForm,
  setB,
  setBw,
  setF,
  updateField,
  fieldErrors,
  requiredStatus,
  competitionOptions,
  teamOptions,
  opponentOptions = [],
  seasonOptions,
  onBack,
  onChangeGround,
  onSave,
  saveState, // "idle" | "saving" | "failed"
  saveErrorMessage,
  // Issue #1028 — the stored surface's own label (`facilityOptionLabel`
  // over the edited match's `facility_name`/`facility_surface`), passed
  // down exactly as `PerformanceTracking.jsx` computes it for the old
  // dialog's own `FacilityPicker` call: used only when the venue's
  // current list can't be read. `null` on a fresh log and on any match
  // that recorded no surface.
  storedLabel = null,
  // Issue #1008 (D203.1, PR #1025 review F1) — forwarded to the "Change
  // ground" `GroundPicker` below. This component is mounted only while
  // `PerformanceTracking.jsx`'s own `newUi("log-scorecard")` gate is true
  // (`showLogScorecardStep`, via `LogScorecardFlow`), which is where this
  // prop is actually read; default false keeps every other render of this
  // component (there are none today, but the default still matters — see
  // `GroundPicker.jsx`'s own header) on the legacy behaviour.
  hideIndoor = false,
}) {
  const [moreOpen, setMoreOpen] = useState(false);
  const [pickingGround, setPickingGround] = useState(false);
  const [groundListOpen, setGroundListOpen] = useState(false);
  // #1364 — the ground the form held when "Change" opened the picker, so Cancel
  // can put it back. Typing in the picker clears `ground_id`; without this,
  // Cancel left the typed text on the card with no ground behind it.
  const groundBeforePicking = useRef(null);
  // #1299 (D303): Save scrolls to the first missing field and focuses it.
  const rootRef = useRef(null);
  const armFocus = useFocusFirstInvalid(rootRef, fieldErrors);
  // Issue #758 §5 / #704 — the wicketkeeper tick. NOW a real, saved column
  // (`matches.kept_wicket`): read straight off `form` (top-level, alongside
  // `ground_id`/`match_time`) so it rides into the payload with everything
  // else `create`/`update` sends (`PerformanceTracking.jsx`'s `payload =
  // { ...form, ... }`), and so an edit opens with the saved value rather
  // than always starting unticked. `=== true`, not truthy: `null` ("never
  // asked") and `false` ("ticked, answered no") must not look the same as
  // an untouched control on a fresh log.
  const keptWicket = form.kept_wicket === true;

  // PR #841 review, D102 — display-only, one decimal for strike rate (the
  // drawn main state reads "82.9"), two for economy. `rateOrNull` itself
  // is untouched: the payload sent to the API still carries its own
  // two-decimal round-trip via `rateOrNull`/`counterOrNull` in
  // `PerformanceTracking.jsx`'s `create()`, unaffected by how this screen
  // chooses to render it.
  const strikeRate = rateOrNull(form.batting.runs, form.batting.balls_faced, 100);
  const strikeRateDisplay = formatRateDisplay(strikeRate, 1);
  const strikeRateHelp = strikeRateHelpText(form.batting.runs, form.batting.balls_faced);

  // #860: overs are overs and balls, so the economy divides by balls.
  const economy = economyOrNull(form.bowling.runs_conceded, form.bowling.overs);
  const economyDisplay = formatRateDisplay(economy, 2);
  const economyHelp = economyHelpText(form.bowling.overs);

  const isEdit = mode === "edit";
  // #1150: a planned match whose day has passed — the same step headed "How did it go?".
  const isFinish = mode === "finish";
  const backLabel = LOG_STEP2_BACK_LABEL;

  // #1355: `GroundPicker.onChange` fires on every keystroke too (with a null
  // `groundId`), and this used to close the picker on the first one. The typed
  // text still reaches the form; the picker closes only on a pick, which is a
  // call that carries a ground id.
  const handleGroundPicked = (name, groundId) => {
    setForm((prev) => ({
      ...prev,
      ground: name,
      ground_id: groundId,
      facility_id:
        facilityAfterGroundChange(prev.ground_id, groundId, prev.facility_id) ??
        // #1373: typing reported (text, null) and dropped facility_id; a pick
        // of the ground the match already had (a blur that resolves the typed
        // name) puts it back.
        (groundId && groundId === groundBeforePicking.current?.ground_id ? groundBeforePicking.current.facility_id ?? null : null),
    }));
    if (groundId) {
      setPickingGround(false);
      setGroundListOpen(false);
    }
  };

  const openGroundPicker = () => {
    groundBeforePicking.current = {
      ground: form.ground,
      ground_id: form.ground_id,
      facility_id: form.facility_id,
    };
    setPickingGround(true);
  };

  const cancelGroundPicker = () => {
    const before = groundBeforePicking.current;
    groundBeforePicking.current = null;
    if (before) setForm((prev) => ({ ...prev, ...before }));
    setPickingGround(false);
    setGroundListOpen(false);
  };

  return (
    <div ref={rootRef} data-testid="log-step-2" className="nw-page-stack" style={{ padding: "var(--nw-space-page)" }}>
      <SecondaryAction
        variant="link"
        onClick={() => {
          markNav("back");
          onBack();
        }}
        data-testid="log-step-2-back"
        style={{
          // Block-level, as the old `display: block` button was, so the
          // box sits in the stack's flow and its footprint stays one line.
          display: "flex",
          width: "fit-content",
          justifyContent: "flex-start",
          textAlign: "left",
          alignSelf: "flex-start",
          textDecoration: "underline",
          fontWeight: 400,
          fontSize: 12,
          lineHeight: 1.5,
          color: "var(--nw-ink-muted)",
        }}
      >
        {backLabel}
      </SecondaryAction>

      {isEdit && (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--nw-space-xtight)" }}>
          <div className="nw-eyebrow">{EDIT_EYEBROW}</div>
          <div style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 22, color: "var(--nw-ink)" }}>
            {EDIT_TITLE}
          </div>
        </div>
      )}

      {isFinish && (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--nw-space-xtight)" }}>
          <div className="nw-eyebrow">{FINISH_EYEBROW}</div>
          <div data-testid="log-step-2-title" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 22, color: "var(--nw-ink)" }}>
            {FINISH_TITLE}
          </div>
          <div className="nw-lede">{FINISH_LEDE}</div>
        </div>
      )}

      {pickingGround ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--nw-space-xtight)" }}>
          <GroundPicker
            inputTestId="m-ground"
            testIdPrefix="perf-ground"
            value={form.ground}
            groundId={form.ground_id}
            onChange={handleGroundPicked}
            placeholder="e.g. Thornmere Park"
            hideIndoor={hideIndoor}
            markFieldErrors
            autoResolveWhileTyping={false}
            dropdownOffset={CANCEL_ROW_CLEARANCE}
            onOpenChange={setGroundListOpen}
          />
          {/* Editing mode has no step 1 to return to (brief-758.md's own
              "editing-a-match" commentary state) — "Change" opens this
              picker IN PLACE, so it needs its own way to back out without
              picking anything. */}
          <SecondaryAction
            variant="link"
            onClick={cancelGroundPicker}
            data-testid="log-step-2-cancel-ground"
            data-list-open={groundListOpen ? "true" : undefined}
            style={{
              // #1373 (D382): the 44px tap box (D253's link fix), drawn text
              // unchanged. The box starts at the field's bottom edge (the
              // stack gap becomes its top padding) so it overlaps nothing;
              // the list opens below it (`dropdownOffset`).
              display: "flex",
              width: "fit-content",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              alignSelf: "flex-start",
              marginBlock: 0,
              marginTop: "calc(-1 * var(--nw-space-xtight))",
              paddingTop: "var(--nw-space-xtight)",
              marginBottom: groundListOpen ? `calc(${CANCEL_FOOTPRINT} + ${CANCEL_OPEN_SHIFT})` : CANCEL_FOOTPRINT,
              textDecoration: "underline",
              fontFamily: "'DM Sans', system-ui, sans-serif",
              fontWeight: 400,
              fontSize: CANCEL_FONT_PX,
              lineHeight: 1.5,
              color: "var(--nw-ink-muted)",
            }}
          >
            Cancel
          </SecondaryAction>
        </div>
      ) : (
        <>
          <ConfirmedRow
            data-testid="log-step-2-ground"
            variant="card"
            eyebrow={GROUND_EYEBROW}
            value={form.ground}
            onChange={onChangeGround || openGroundPicker}
            changeLabel={CHANGE_LABEL}
            changeTestId="log-step-2-change-ground"
            // #1309 (D303): a missing ground gets the 2px alert-red border on the card.
            invalid={!!fieldErrors.ground_id}
            style={fieldErrors.ground_id ? { border: "var(--nw-border-invalid)" } : undefined}
            changeButtonProps={
              fieldErrors.ground_id ? { "data-field-invalid": "true", "aria-describedby": "m-ground-error" } : undefined
            }
          />
          {/* Issue #1028 (#842 part A, D103/D129) — the field/net pick,
              directly under the confirmed ground: `facility_id` is a
              column of the match row, saved with it, and the ground it
              depends on is confirmed right above. The shared
              `FacilityPicker` (#546) already renders nothing for a
              ground with no recorded surfaces (D135) and treats a failed
              list read as empty (D137) — see its own header — so no extra
              gating belongs here; a second `useGroundFacilities` read
              would only be a second, redundant request for the same
              answer it already gives. `tone="quiet"` is D55's chip
              (ink only, no alert colours); neither of the other two
              `FacilityPicker` call sites passes it, so both keep their
              original colours — the OLD MATCH DIALOG's own call
              (`PerformanceTracking.jsx`, flag-off leg, `m-facility`) and
              the training form's (`TrainingSessions.jsx:607`,
              `ts-facility`). Review F8: this comment previously called
              `PerformanceTracking.jsx`'s call "the training dialog's",
              which is the wrong one of the two. */}
          <FacilityPicker
            testId="m-facility"
            groundId={form.ground_id}
            value={form.facility_id}
            storedLabel={storedLabel}
            tone="quiet"
            onChange={(facilityId) => setForm((prev) => ({ ...prev, facility_id: facilityId }))}
          />
        </>
      )}
      {fieldErrors.ground_id && (
        <FieldError id="m-ground-error" testId="m-ground-error">{fieldErrors.ground_id}</FieldError>
      )}

      {/* #860: an overs error is not a missing field; it has its own words under the box. */}
      {Object.keys(fieldErrors).some((k) => k !== "overs") && (
        <ErrorBanner>{REQUIRED_SUMMARY}</ErrorBanner>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "var(--nw-space-inline)" }}>
        <Group>
          <GroupLabel htmlFor="m-date">Date</GroupLabel>
          <Field
            id="m-date"
            data-testid="m-date"
            type="date"
            value={form.match_date}
            onChange={(e) => updateField("match_date", e.target.value)}
            error={fieldErrors.match_date}
          />
        </Group>
        <Group>
          <GroupLabel htmlFor="m-comp">Competition</GroupLabel>
          <Field
            id="m-comp"
            data-testid="m-comp"
            list="scorecard-comp-suggestions"
            placeholder={COMP_HELPER}
            value={form.competition}
            onChange={(e) => updateField("competition", e.target.value)}
            error={fieldErrors.competition}
          />
          <datalist id="scorecard-comp-suggestions">
            {competitionOptions.map((c) => <option key={c} value={c} />)}
          </datalist>
        </Group>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "var(--nw-space-inline)" }}>
        <Group>
          <GroupLabel htmlFor="m-team">Your team</GroupLabel>
          <Field
            id="m-team"
            data-testid="m-team"
            list="scorecard-team-suggestions"
            value={form.team}
            onChange={(e) => updateField("team", e.target.value)}
          />
          <datalist id="scorecard-team-suggestions">
            {teamOptions.map((t) => <option key={t} value={t} />)}
          </datalist>
        </Group>
        <Group>
          <GroupLabel htmlFor="m-opp">Opposition</GroupLabel>
          <Field
            id="m-opp"
            data-testid="m-opp"
            list="scorecard-opp-suggestions"
            placeholder={OPP_PLACEHOLDER}
            value={form.opponent}
            onChange={(e) => updateField("opponent", e.target.value)}
            error={fieldErrors.opponent}
          />
          <datalist id="scorecard-opp-suggestions">
            {opponentOptions.map((o) => <option key={o} value={o} />)}
          </datalist>
        </Group>
      </div>

      <DisclosureRow
        title={MORE_TITLE}
        subLabel={MORE_SUBLABEL}
        onClick={() => setMoreOpen((v) => !v)}
        data-testid="log-step-2-more"
        aria-expanded={moreOpen}
      />
      {moreOpen && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "var(--nw-space-inline)" }}>
          <Group>
            <GroupLabel htmlFor="m-time">Start time</GroupLabel>
            <Field id="m-time" data-testid="m-time" type="time" value={form.match_time} onChange={(e) => updateField("match_time", e.target.value)} />
          </Group>
          <Group>
            <GroupLabel htmlFor="m-format">Format</GroupLabel>
            <select
              id="m-format"
              data-testid="m-format"
              value={form.match_format}
              onChange={(e) => updateField("match_format", e.target.value)}
              className="nw-kit-input"
              style={{ width: "100%", height: "var(--nw-size-control)", borderRadius: "var(--nw-radius-control)", border: "1px solid var(--nw-line)", background: "var(--nw-surface)", padding: "0 12px", fontSize: 15, color: "var(--nw-ink)" }}
            >
              {["T10", "T20", "T20I", "Limited Overs 30", "Limited Overs 40", "List A", "ODI", "First-Class", "Test"].map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
          </Group>
          <Group style={{ gridColumn: "1 / -1" }}>
            <GroupLabel htmlFor="m-season">Season</GroupLabel>
            <Field
              id="m-season"
              data-testid="m-season"
              list="perf-season-suggestions"
              value={form.season}
              onChange={(e) => updateField("season", e.target.value)}
              placeholder="e.g. 2025/26 (auto if blank)"
            />
            <datalist id="perf-season-suggestions">
              {seasonOptions.map((s) => <option key={s} value={s} />)}
            </datalist>
          </Group>
        </div>
      )}

      <Card>
        <SectionLabel>{BATTING_LABEL}</SectionLabel>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "var(--nw-space-grid)" }}>
          <BattingPositionSelect data-testid="m-bat-pos" value={form.batting.batting_position} onChange={(v) => setB("batting_position", v)} />
          <Counter label="Runs" data-testid="m-runs" value={form.batting.runs} onChange={(e) => setB("runs", e.target.value)} />
          <Counter label="Balls" data-testid="m-balls" value={form.batting.balls_faced} onChange={(e) => setB("balls_faced", e.target.value)} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "var(--nw-space-grid)" }}>
          <Counter label="Fours" data-testid="m-fours" value={form.batting.fours} onChange={(e) => setB("fours", e.target.value)} />
          <Counter label="Sixes" data-testid="m-sixes" value={form.batting.sixes} onChange={(e) => setB("sixes", e.target.value)} />
          <DismissalSelect data-testid="m-dismissal" value={form.batting.dismissal_type} onChange={(v) => setForm({ ...form, batting: { ...form.batting, dismissal_type: v, not_out: v === "Not Out" } })} />
        </div>
        <DerivedRateRow label={STRIKE_RATE_LABEL} display={strikeRateDisplay} data-testid="log-step-2-strike-rate" />
        <div className="nw-help">{strikeRateHelp}</div>
      </Card>

      <Card>
        <SectionLabel>{BOWLING_LABEL}</SectionLabel>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "var(--nw-space-grid)" }}>
          <Counter
            label="Overs"
            data-testid="m-overs"
            value={form.bowling.overs}
            onChange={(e) => setBw("overs", e.target.value)}
            style={fieldErrors.overs ? { border: "var(--nw-border-invalid)" } : undefined}
            aria-describedby={fieldErrors.overs ? "m-overs-error" : undefined}
            aria-invalid={fieldErrors.overs ? true : undefined}
          />
          <Counter label="Maidens" data-testid="m-maidens" value={form.bowling.maidens} onChange={(e) => setBw("maidens", e.target.value)} />
          <Counter label={RUNS_CONCEDED_LABEL} data-testid="m-runs-conceded" value={form.bowling.runs_conceded} onChange={(e) => setBw("runs_conceded", e.target.value)} />
        </div>
        {fieldErrors.overs && <FieldError id="m-overs-error" testId="m-overs-error">{fieldErrors.overs}</FieldError>}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "var(--nw-space-grid)" }}>
          <Counter label="Wickets" data-testid="m-wickets" value={form.bowling.wickets} onChange={(e) => setBw("wickets", e.target.value)} />
          <Counter label="Dot balls" data-testid="m-dot-balls" value={form.bowling.dot_balls} onChange={(e) => setBw("dot_balls", e.target.value)} />
          <Counter label="Wides" data-testid="m-wides" value={form.bowling.wides} onChange={(e) => setBw("wides", e.target.value)} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "var(--nw-space-grid)" }}>
          <Counter label="No balls" data-testid="m-no-balls" value={form.bowling.no_balls} onChange={(e) => setBw("no_balls", e.target.value)} />
        </div>
        <DerivedRateRow label={ECONOMY_LABEL} display={economyDisplay} data-testid="log-step-2-economy" />
        <div className="nw-help">{economyHelp}</div>
      </Card>

      <Card>
        <SectionLabel>{FIELDING_LABEL}</SectionLabel>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "var(--nw-space-grid)" }}>
          <Counter label="Catches" data-testid="m-fielding-catches" value={form.fielding.fielding_catches} onChange={(e) => setF("fielding_catches", e.target.value)} />
          <Counter label="Run outs" data-testid="m-run-outs" value={form.fielding.run_outs} onChange={(e) => setF("run_outs", e.target.value)} />
          <Counter label="Run-out assists" data-testid="m-assists" value={form.fielding.assists} onChange={(e) => setF("assists", e.target.value)} />
          <Counter label="Direct hits" data-testid="m-direct-hit-run-outs" value={form.fielding.direct_hit_run_outs} onChange={(e) => setF("direct_hit_run_outs", e.target.value)} />
        </div>
        {/* PR #841 review — one full-width line per group, directly under
            the counter grid and above the group's own help line, not one
            beside each of the two #715 catches boxes (crowded, never the
            drawn width). */}
        <div className="nw-foot">{CATCHES_ZERO_NOTE}</div>
        <div className="nw-help">{FIELDING_HELP}</div>
      </Card>

      {/* #1394 (D221 G4, D357): the row is a `<label>`, so the whole 56px row is the
          tap target for the switch, which stays the drawn 44x26. */}
      <label
        className="flex items-center justify-between"
        style={{ cursor: "pointer", minHeight: 56, padding: "10px 14px", background: "var(--nw-surface)", border: "1px solid var(--nw-navy)", borderRadius: "var(--nw-radius-card)" }}
      >
        {/* Spans, not divs: a label holds phrasing content only. The switch is named by the
            title alone and described by the sublabel. */}
        <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span id="m-kept-wicket-title" style={{ display: "block", fontWeight: 600, fontSize: 15, color: "var(--nw-ink)" }}>{KEEPER_LABEL}</span>
          <span id="m-kept-wicket-desc" style={{ display: "block", fontSize: 12, color: "var(--nw-ink-muted)" }}>{KEEPER_SUBLABEL}</span>
        </span>
        <ToggleSwitch data-testid="m-kept-wicket" aria-labelledby="m-kept-wicket-title" aria-describedby="m-kept-wicket-desc" checked={keptWicket} onChange={(v) => updateField("kept_wicket", !!v)} />
      </label>

      {keptWicket && (
        <Card>
          <SectionLabel>{KEEPER_GROUP_LABEL}</SectionLabel>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "var(--nw-space-grid)" }}>
            <Counter label="Caught behind" data-testid="m-wk-wicketkeeping-catches" value={form.fielding.wicketkeeping_catches} onChange={(e) => setF("wicketkeeping_catches", e.target.value)} />
            <Counter label="Stumpings" data-testid="m-wk-stumpings" value={form.fielding.stumpings} onChange={(e) => setF("stumpings", e.target.value)} />
            <Counter label="Byes" data-testid="m-wk-byes-conceded" value={form.fielding.byes_conceded} onChange={(e) => setF("byes_conceded", e.target.value)} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "var(--nw-space-grid)" }}>
            <Counter label="Run outs (wk)" data-testid="m-wk-wicketkeeping-run-outs" value={form.fielding.wicketkeeping_run_outs} onChange={(e) => setF("wicketkeeping_run_outs", e.target.value)} />
            <Counter label="Assists (wk)" data-testid="m-wk-wicketkeeping-assists" value={form.fielding.wicketkeeping_assists} onChange={(e) => setF("wicketkeeping_assists", e.target.value)} />
          </div>
          <div className="nw-foot">{CATCHES_ZERO_NOTE}</div>
          <div className="nw-help">{KEEPER_GROUP_HELP}</div>
        </Card>
      )}

      <div
        className="flex items-start"
        style={{ gap: 8, padding: "12px 14px", background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-control)" }}
      >
        <Info size={16} style={{ color: "var(--nw-ink-muted)", marginTop: 2, flexShrink: 0 }} aria-hidden="true" />
        <span className="nw-help">{FOOTER_NOTE}</span>
      </div>

      {saveState === "failed" && (
        <>
          {/* PR #841 review, B3 (D57) — the API's own `detail`, not a fixed
              string; `saveErrorMessage` falls back to the same title this
              banner always showed when the API gives no detail at all. No
              toast accompanies this on the new-UI path (see `create`'s own
              catch block, PerformanceTracking.jsx) — a toast only ever
              confirms a success here now. */}
          <ErrorBanner testId="m-save-failed">{saveErrorMessage || (isFinish ? FINISH_SAVE_FAILED : SAVE_FAILED_TITLE)}</ErrorBanner>
          {/* Finishing with no reason from the API: the banner is already the board's whole sentence. */}
          {!(isFinish && !saveErrorMessage) && <div className="nw-help">{isFinish ? FINISH_SAVE_FAILED_BODY : SAVE_FAILED_BODY}</div>}
        </>
      )}

      <ActionButton
        onClick={() => {
          armFocus();
          onSave();
        }}
        disabled={saveState === "saving" || !canSubmitRequired(requiredStatus)}
        data-testid="m-save"
        icon={saveState === "saving" ? <Loader2 size={18} className="animate-spin" /> : null}
        // PR #841 review, B4 — `ActionButton`'s own `disabled` styling dims
        // to opacity 0.6 (its header comment: shadcn parity), which is
        // correct everywhere else disabled means "cannot be pressed yet"
        // but wrong here: `DirectionA2.dc.html:176` draws the saving state
        // at full gold, still disabled (nothing double-submits), just not
        // dimmed. Scoped to this one call site's `style` override, not a
        // change to the shared component's own disabled default. Round 5
        // conformance, D27 (issue #864) — the label's own ink colour no
        // longer needs a per-call override, only opacity does.
        style={saveState === "saving" ? { opacity: 1 } : undefined}
      >
        {saveState === "saving" ? SAVING_LABEL : isFinish ? SAVE_LABEL_FINISH : isEdit ? SAVE_LABEL_EDIT : SAVE_LABEL}
      </ActionButton>
    </div>
  );
}
